import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateClientSatisfactionSurveyTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const contactInfoId = uuidv4();
  const contactInfoBlockId = uuidv4();
  const emailId = uuidv4();
  const emailBlockId = uuidv4();
  const dropdownId = uuidv4();
  const dropdownBlockId = uuidv4();
  const ratingId = uuidv4();
  const ratingBlockId = uuidv4();
  const multipleChoiceId = uuidv4();
  const multipleChoiceBlockId = uuidv4();
  const optionScaleId = uuidv4();
  const optionScaleBlockId = uuidv4();
  const longTextId = uuidv4();
  const longTextBlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for select options
  const dropdownOption1Id = uuidv4();
  const dropdownOption2Id = uuidv4();
  const dropdownOption3Id = uuidv4();
  const mcOption1Id = uuidv4();
  const mcOption2Id = uuidv4();
  const mcOption3Id = uuidv4();
  const mcOption4Id = uuidv4();

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const ratingMediaId = uuidv4();
  const endScreenMediaId = uuidv4();

  return {
    ...themes.darkMidnight,
    "collectionId": collectionId,
    "name": "Client Satisfaction Survey",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "MEDIUM",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_INLINE_CENTER",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "How was your experience with us?",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Your feedback helps us elevate the support we provide.",
        "thirdContent": "Start survey",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 50,
        "media": {
          "provider": "PEXEL",
          "id": welcomeMediaId,
          "type": "video",
          "name": "6390166",
          "link": "https://videos.pexels.com/video-files/6390166/6390166-hd_1280_720_25fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/6390166/pictures/preview-0.jpg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": contactInfoId,
        "blockId": contactInfoBlockId,
        "content": "First, what's your name?",
        "manditory": true,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We like to keep things personal.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": true
          },
          "last_name": {
            "required": false,
            "visible": false
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": false
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "EMAIL",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": emailId,
        "blockId": emailBlockId,
        "content": `Thanks, {{${contactInfoBlockId}__first_name}}. What's your email?`,
        "manditory": true,
        "order": 2,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll only reach out if we have follow-up questions.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "DROPDOWN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": dropdownId,
        "blockId": dropdownBlockId,
        "content": "Which service did you book with us?",
        "manditory": true,
        "order": 3,
        "selectOptions": [
          {
            "id": dropdownOption1Id,
            "order": 0,
            "value": "Private training session"
          },
          {
            "id": dropdownOption2Id,
            "order": 1,
            "value": "Nutrition consultation"
          },
          {
            "id": dropdownOption3Id,
            "order": 2,
            "value": "Wellness coaching"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "RATING",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_INLINE_TOP",
        "id": ratingId,
        "blockId": ratingBlockId,
        "content": "How would you rate your overall experience?",
        "manditory": true,
        "order": 4,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": 5,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "",
        "thirdContent": null,
        "forthContent": null,
        "icon": "star",
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": ratingMediaId,
          "type": "image",
          "name": "greyscale photo of woman in bralette inside room",
          "link": "https://unsplash.com/photos/5UbIqV58CW8/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxMnx8Z3ltfGVufDB8fHx8MTc2NDU0NjA5OHww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Sushil Ghimire",
          "authorProfile": "https://unsplash.com/@hipcravo",
          "downloadLink": "https://api.unsplash.com/photos/5UbIqV58CW8/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxMnx8Z3ltfGVufDB8fHx8MTc2NDU0NjA5OHww"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoiceId,
        "blockId": multipleChoiceBlockId,
        "content": "And how was your coach or specialist?",
        "manditory": true,
        "order": 5,
        "selectOptions": [
          {
            "id": mcOption1Id,
            "order": 0,
            "value": "Knowledgeable"
          },
          {
            "id": mcOption2Id,
            "order": 1,
            "value": "Supportive"
          },
          {
            "id": mcOption3Id,
            "order": 2,
            "value": "Motivating"
          },
          {
            "id": mcOption4Id,
            "order": 3,
            "value": "Easy to talk to"
          }
        ],
        "multiSelect": true,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Select any that apply.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "OPTION_SCALE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": optionScaleId,
        "blockId": optionScaleBlockId,
        "content": "Almost there! How likely are you to recommend us to someone else?",
        "manditory": true,
        "order": 6,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": 0,
        "maxRange": 10,
        "minLabel": "Not likely at all",
        "maxLabel": "Extremely likely",
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": longTextId,
        "blockId": longTextBlockId,
        "content": "Anything else you'd like to share before you go?",
        "manditory": false,
        "order": 7,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Your input helps shape a better experience for everyone.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_INLINE_TOP",
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "content": "Thanks for the feedback!",
        "manditory": false,
        "order": 8,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We appreciate you taking the time.",
        "thirdContent": "Visit Our Website",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 52,
        "media": {
          "provider": "UNSPLASH",
          "id": endScreenMediaId,
          "type": "image",
          "name": "A man and a woman working out in a gym",
          "link": "https://unsplash.com/photos/G6svDyPVRrc/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwyMHx8cGVyc29uYWwlMjB0cmFpbmluZ3xlbnwwfHx8fDE3NjQ1NDYxOTJ8MA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Sergio Kian",
          "authorProfile": "https://unsplash.com/@sergiokian",
          "downloadLink": "https://api.unsplash.com/photos/G6svDyPVRrc/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwyMHx8cGVyc29uYWwlMjB0cmFpbmluZ3xlbnwwfHx8fDE3NjQ1NDYxOTJ8MA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      }
    ],
    "snippets": [],
    "metaTitle": "Client Satisfaction Survey",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateClientSatisfactionSurveyTemplate;